import * as THREE from "three/webgpu";
import { celestialTheme } from "../../design/celestialTheme";
import { WORLD_ORIGIN } from "../config/worldFrame";
import type { PresentedNode, PresentedOrbital, PresentedSnapshot } from "../../presentation/mesh";
import type { EdgeSpatialProjection } from "../../domain/mesh";
import type { FocusState } from "../../store/nexusStore";
import { focusedNodeId, focusedOrbitalId } from "../../store/nexusStore";
import type { AttentionField } from "../attention/AttentionField";
import type { ResourceRegistry } from "../core/ResourceRegistry";
import type { RelationPhysics } from "../relations/relationPhysics";
import type { SpatialEntityRegistry } from "../spatial/SpatialEntityRegistry";
import type { SpatialProjectionPipeline } from "../spatial/SpatialProjectionPipeline";
import type { OrbitalTrackProjector, OrbitalTrackRuntime } from "./OrbitalTrackProjector";
import type { RelationEdgeRuntime, RelationPath } from "./RelationPathProjector";
import type { RelationPathProjector } from "./RelationPathProjector";
import type { RelationRegistry } from "../relations/RelationRegistry";
import { configureInteractionObject } from "../interaction/interactionLayer";
import { addCelestialOntologyAccent } from "./CelestialOntologyFactory";
import { createStellarGlow, createStellarGlowTexture, STELLAR_GLOW_SCALE } from "./StellarGlowFactory";
import {
  createAnchoredOrbitDrift,
  sampleAnchoredOrbitDrift,
  type AnchoredOrbitDrift,
} from "./AnchoredOrbitDrift";

const DEFAULT_EVENT_ROUTE: EdgeSpatialProjection = {
  bend: 0.03,
  skew: 0.015,
  lift: 0.08,
  thickness: 0.006,
  opacityScale: 1,
};

export type NodeRuntime = {
  node: PresentedNode;
  root: THREE.Group;
  core: THREE.Mesh;
  glow: THREE.Sprite;
  interaction: THREE.Mesh;
  coreMaterial: THREE.MeshBasicNodeMaterial;
  glowMaterial: THREE.SpriteNodeMaterial;
  glowVisibility: number;
  drift: AnchoredOrbitDrift | null;
  driftClock: number;
  driftSpeedMix: number;
};

type NodeBuildContext = {
  resources: ResourceRegistry;
  positions: SpatialEntityRegistry;
  spatial: SpatialProjectionPipeline;
  visualAnchors: Map<string, THREE.Vector3>;
  nodeRuntimes: Map<string, NodeRuntime>;
  interactive: THREE.Object3D[];
  root: THREE.Group;
};

export function buildWorldNodes(
  nodes: PresentedNode[],
  layout: ReadonlyMap<string, THREE.Vector3>,
  context: NodeBuildContext,
) {
  const sphereGeometry = context.resources.geometry(new THREE.SphereGeometry(0.18, 28, 28));
  const hitGeometry = context.resources.geometry(new THREE.SphereGeometry(0.52, 20, 20));
  const hitMaterial = context.resources.material(makeWorldMaterial(celestialTheme.background, 1, 0));
  const glowTexture = createStellarGlowTexture(context.resources);
  let singularityNodeId: string | null = null;

  for (const node of nodes) {
    const authoring =
      node.archetype === "black-hole" ? WORLD_ORIGIN.clone() : (layout.get(node.id)?.clone() ?? WORLD_ORIGIN.clone());
    const semantic =
      node.archetype === "black-hole"
        ? authoring.clone()
        : context.spatial.toSemantic(authoring, authoring.clone(), "anchor");
    const visual = semantic.clone();
    context.positions.register(node.id, authoring, semantic, visual);
    context.visualAnchors.set(node.id, visual);
    if (node.archetype === "black-hole") singularityNodeId = node.id;

    const root = new THREE.Group();
    root.position.copy(visual);
    root.userData.entityId = node.id;
    root.userData.entityKind = "node";
    const palette = celestialTheme.clusters[node.cluster];
    const coreColor =
      node.status === "warning"
        ? celestialTheme.status.warning
        : node.status === "draft"
          ? celestialTheme.status.draft
          : palette.core;
    const coreMaterial = context.resources.material(makeWorldMaterial(coreColor, 2.2, 1));
    const stellarScale = node.archetype === "black-hole" ? 0.001 : node.magnitude;
    const { glow, material: glowMaterial } = createStellarGlow(
      context.resources,
      glowTexture,
      palette.secondary,
      node.archetype === "black-hole" ? 0 : 0.13,
      stellarScale,
    );
    const core = new THREE.Mesh(sphereGeometry, coreMaterial);
    core.scale.setScalar(stellarScale);
    glow.visible = node.archetype !== "black-hole";
    const interaction = new THREE.Mesh(hitGeometry, hitMaterial);
    configureInteractionObject(interaction);
    interaction.scale.setScalar(node.archetype === "black-hole" ? 3.2 : Math.max(1.4, node.magnitude * 1.55));
    for (const object of [core, glow, interaction]) {
      object.userData.entityId = node.id;
      object.userData.entityKind = "node";
    }
    root.add(core, glow, interaction);
    addCelestialOntologyAccent(root, node, context.resources);
    context.root.add(root);
    context.interactive.push(interaction);
    context.nodeRuntimes.set(node.id, {
      node,
      root,
      core,
      glow,
      interaction,
      coreMaterial,
      glowMaterial,
      glowVisibility: 1,
      drift: node.archetype === "black-hole" ? null : createAnchoredOrbitDrift(node, authoring),
      driftClock: 0,
      driftSpeedMix: node.archetype === "black-hole" ? 0 : 1,
    });
  }

  return singularityNodeId;
}

export type EdgeRuntime = RelationEdgeRuntime & {
  id: string;
  source: string;
  target: string;
  physics: RelationPhysics;
  phase: number;
};

export type OrbitalRuntime = {
  orbital: PresentedOrbital;
  satellite: THREE.Mesh;
  interaction: THREE.Mesh;
  track: OrbitalTrackRuntime;
  quaternion: THREE.Quaternion;
  localAuthoring: THREE.Vector3;
};

export function makeWorldMaterial(color: string, intensity: number, opacity: number) {
  const material = new THREE.MeshBasicNodeMaterial();
  material.color = new THREE.Color(color).multiplyScalar(intensity);
  material.opacity = opacity;
  material.transparent = true;
  material.depthWrite = opacity >= 0.99;
  material.toneMapped = false;
  return material;
}

export function createFallbackWorldRelation(
  sourceId: string,
  targetId: string,
  positions: SpatialEntityRegistry,
  projector: RelationPathProjector,
  relations: RelationRegistry,
) {
  const source = positions.get(sourceId);
  const target = positions.get(targetId);
  if (!source || !target) return null;
  const path = projector.createPath(
    source.authoring,
    target.authoring,
    source.semantic,
    target.semantic,
    DEFAULT_EVENT_ROUTE,
  );
  relations.register(sourceId, targetId, path);
  const binding = relations.binding(sourceId, targetId);
  if (!binding) return null;
  return {
    binding,
    livePath: { path, source: sourceId, target: targetId, route: DEFAULT_EVENT_ROUTE } satisfies LiveRelationPath,
  };
}

type OrbitalBuildContext = {
  resources: ResourceRegistry;
  positions: SpatialEntityRegistry;
  orbitalTrackProjector: OrbitalTrackProjector;
  visualAnchors: Map<string, THREE.Vector3>;
  orbitalRuntimes: Map<string, OrbitalRuntime>;
  interactive: THREE.Object3D[];
  root: THREE.Group;
};

export function buildWorldOrbitals(snapshot: PresentedSnapshot, context: OrbitalBuildContext) {
  const satelliteGeometry = context.resources.geometry(new THREE.SphereGeometry(0.11, 20, 20));
  const hitGeometry = context.resources.geometry(new THREE.SphereGeometry(0.3, 16, 16));
  const hitMaterial = context.resources.material(makeWorldMaterial(celestialTheme.background, 1, 0));
  for (const orbital of snapshot.orbitalNodes) {
    const parent = context.positions.get(orbital.parentId);
    if (!parent) continue;
    const authoring = parent.authoring.clone();
    const semantic = parent.semantic.clone();
    const visual = parent.visual.clone();
    context.positions.register(orbital.id, authoring, semantic, visual);
    context.visualAnchors.set(orbital.id, visual);
    const palette = celestialTheme.orbitals[orbital.visual.tone];
    const satellite = new THREE.Mesh(
      satelliteGeometry,
      context.resources.material(makeWorldMaterial(palette.core, 2.1, 1)),
    );
    satellite.scale.setScalar(0.7 + orbital.visual.scale * 2.4);
    satellite.visible = false;
    const interaction = new THREE.Mesh(hitGeometry, hitMaterial);
    configureInteractionObject(interaction);
    for (const object of [satellite, interaction]) {
      object.userData.entityId = orbital.id;
      object.userData.entityKind = "orbital";
      object.userData.parentId = orbital.parentId;
    }
    const track = context.orbitalTrackProjector.create(orbital, celestialTheme.guides.idle);
    context.resources.geometry(track.geometry);
    context.resources.material(track.material);
    context.root.add(track.line, satellite, interaction);
    context.interactive.push(interaction);
    context.orbitalRuntimes.set(orbital.id, {
      orbital,
      satellite,
      interaction,
      track,
      quaternion: new THREE.Quaternion().setFromEuler(new THREE.Euler(orbital.orbit.tiltX, 0, orbital.orbit.tiltZ)),
      localAuthoring: new THREE.Vector3(),
    });
  }
}

export type LiveRelationPath = {
  path: RelationPath;
  source: string;
  target: string;
  route: EdgeSpatialProjection;
};

export type SemanticUpdateContext = {
  attention: AttentionField;
  focus: FocusState;
  hoveredEntityId: string | null;
  reducedMotion: boolean;
  accessibleMode: boolean;
  relatedIds: ReadonlySet<string>;
  nodeRuntimes: Map<string, NodeRuntime>;
  edgeRuntimes: EdgeRuntime[];
  orbitalRuntimes: Map<string, OrbitalRuntime>;
  positions: SpatialEntityRegistry;
  spatial: SpatialProjectionPipeline;
  relationProjector: RelationPathProjector;
  relationPaths: LiveRelationPath[];
  scaleTarget: THREE.Vector3;
};

function targetDriftSpeedMix(context: SemanticUpdateContext, runtime: NodeRuntime) {
  if (context.reducedMotion || context.accessibleMode) return 0;
  if (context.focus.kind !== "cosmos") return 0;
  if (context.hoveredEntityId === runtime.node.id) return 0;
  if (context.hoveredEntityId !== null) return 0.15;
  return 1;
}

export function updateWorldSemantic(context: SemanticUpdateContext, delta: number, elapsed: number) {
  context.attention.update(delta);
  const focusNode = focusedNodeId(context.focus);
  const focusOrbital = focusedOrbitalId(context.focus);
  let relationGeometryDirty = false;
  for (const runtime of context.nodeRuntimes.values()) {
    if (runtime.node.archetype === "black-hole") continue;
    const position = context.positions.get(runtime.node.id);
    if (!position) continue;
    const targetSpeed = targetDriftSpeedMix(context, runtime);
    const speedSmoothing = context.reducedMotion || context.accessibleMode ? 1 : 1 - Math.exp(-delta * 5.8);
    runtime.driftSpeedMix += (targetSpeed - runtime.driftSpeedMix) * speedSmoothing;
    if (targetSpeed === 0 && runtime.driftSpeedMix < 0.0005) runtime.driftSpeedMix = 0;
    if (runtime.drift && runtime.driftSpeedMix > 0) {
      runtime.driftClock += delta * runtime.driftSpeedMix;
      sampleAnchoredOrbitDrift(runtime.drift, runtime.driftClock, position.authoring);
      context.spatial.toSemantic(position.authoring, position.semantic, "anchor");
      relationGeometryDirty = true;
    }

    const selected = runtime.node.id === focusNode;
    const hovered = runtime.node.id === context.hoveredEntityId;
    const related = focusNode !== null && context.relatedIds.has(runtime.node.id);
    const attention = context.attention.weight(runtime.node, selected, hovered, related);
    const breathe =
      context.reducedMotion || context.accessibleMode
        ? 1
        : 1 + Math.sin(elapsed * 1.12 + runtime.node.id.length) * 0.025;
    const targetScale = runtime.node.magnitude * breathe * (0.9 + attention * 0.16);
    const smoothing = context.reducedMotion ? 1 : 1 - Math.exp(-delta * 6.2);
    context.scaleTarget.setScalar(targetScale);
    runtime.core.scale.lerp(context.scaleTarget, smoothing);
    context.scaleTarget.setScalar(targetScale * STELLAR_GLOW_SCALE);
    runtime.glow.scale.lerp(context.scaleTarget, smoothing);
    runtime.glowMaterial.opacity = context.attention.glowOpacity(runtime.node, attention) * runtime.glowVisibility;
  }
  if (relationGeometryDirty)
    for (const relation of context.relationPaths) {
      const source = context.positions.get(relation.source);
      const target = context.positions.get(relation.target);
      if (!source || !target) continue;
      context.relationProjector.syncPath(
        relation.path,
        source.authoring,
        target.authoring,
        source.semantic,
        target.semantic,
        relation.route,
      );
    }
  for (const edge of context.edgeRuntimes) {
    if (!edge.line.visible) continue;
    const wave =
      context.reducedMotion || context.accessibleMode
        ? 0
        : Math.sin(elapsed * Math.PI * 2 * edge.physics.activityHz + edge.phase) * edge.physics.activityDepth;
    const focusActive = focusNode !== null && (edge.source === focusNode || edge.target === focusNode);
    edge.material.opacity = Math.max(
      0.012,
      edge.baseOpacity * (1 + wave) * (focusActive ? 1.35 : focusNode ? 0.34 : 1),
    );
  }
  for (const runtime of context.orbitalRuntimes.values()) {
    const visible = runtime.orbital.parentId === focusNode;
    runtime.track.line.visible = context.focus.kind === "orbital" && visible;
    runtime.satellite.visible = visible;
    runtime.interaction.userData.pickEnabled = visible;
    if (!visible) continue;
    const position = context.positions.get(runtime.orbital.id);
    const parent = context.positions.get(runtime.orbital.parentId);
    if (!position || !parent) continue;
    const orbit = runtime.orbital.orbit;
    const angle = orbit.phase + elapsed * (context.reducedMotion ? 0 : orbit.speed);
    const yRadius = orbit.radius * (1 - Math.min(orbit.eccentricity, 0.08) * 0.22);
    runtime.localAuthoring
      .set(Math.cos(angle) * orbit.radius, Math.sin(angle) * yRadius, 0)
      .applyQuaternion(runtime.quaternion)
      .add(parent.authoring);
    position.authoring.copy(runtime.localAuthoring);
    context.spatial.toSemantic(position.authoring, position.semantic, "orbital");
    runtime.satellite.scale.setScalar(
      (0.7 + runtime.orbital.visual.scale * 2.4) * (runtime.orbital.id === focusOrbital ? 1.22 : 1),
    );
    runtime.track.material.opacity =
      runtime.orbital.id === focusOrbital
        ? Math.min((orbit.ringOpacity ?? 0.12) + 0.08, 0.26)
        : Math.min(orbit.ringOpacity ?? 0.12, 0.12);
  }
}
